import { Box, Group, Image, Paper, Stack, Title } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import type { CellContext } from '@tanstack/react-table';
import {
	createFacetedMinMaxValues,
	createFacetedRowModel,
	createFilteredRowModel,
	createPaginatedRowModel,
	createSortedRowModel,
	useTable,
} from '@tanstack/react-table';

import { Search } from '@apple/assets/icons';
import { NoPendingApproval } from '@apple/assets/images';
import { useTranslation } from '@apple/lib/i18next';
import { DataTable } from '@apple/ui/data-table/controls/data-table';
import { createDataTableFeatures, type DataTableFeatures } from '@apple/ui/data-table/features';
import { Link } from '@apple/ui/link/Link';
import { FormattedDate } from '@apple/utils/globalization/components/FormattedDate';
import { DEFAULT_PAGE_INDEX } from '@apple/utils/pagination/models';

import { OrderCommentStatusDisplay } from '@apple/features/order/components/order-comment-status';

import type { OrderApprovalListing } from '../models/approval';
import { getApprovalOrdersOptions } from '../queries/approval';

const features = createDataTableFeatures({
	filteredRowModel: createFilteredRowModel(),
	sortedRowModel: createSortedRowModel(),
	paginatedRowModel: createPaginatedRowModel(),
	facetedRowModel: createFacetedRowModel(),
	facetedMinMaxValues: createFacetedMinMaxValues(),
});

const PAGE_SIZE = 20;

type Props = {
	renderOrderLink: (
		ctx: CellContext<DataTableFeatures, OrderApprovalListing, unknown>,
	) => React.ReactNode;
};

export function OrdersPendingApprovalTable({ renderOrderLink }: Props) {
	const { t } = useTranslation('order-approval');
	const ordersQuery = useQuery(getApprovalOrdersOptions);
	const table = useTable({
		features,
		data: ordersQuery.data,
		enableSorting: true,
		columnResizeMode: 'onChange',
		enableFilters: false,
		enableColumnFilters: false,
		manualFiltering: false,
		manualSorting: false,
		initialState: {
			sorting: [
				{
					id: 'orderDate',
					desc: true,
				},
			],
			pagination: { pageSize: PAGE_SIZE, pageIndex: DEFAULT_PAGE_INDEX },
		},
		columns: [
			{
				accessorKey: 'purchaseOrderNumber',
				header: t('headers.orderNumber'),
				filter: {
					group: t('order:filters.groups.labels.orderInfo'),
					variant: 'text',
				},
				size: 100,
				cell: renderOrderLink,
			},
			{
				accessorKey: 'orderDate',
				header: t('headers.orderDate'),
				enableSorting: true,
				sortFn: 'datetime',
				filter: {
					group: t('order:filters.groups.labels.orderInfo'),
					variant: 'date-range',
				},
				size: 100,
				cell: ({ row }) => <FormattedDate value={row.original.orderDate} humanize />,
			},
			{
				accessorKey: 'appleId',
				header: t('headers.appleId'),
				filter: {
					group: t('order:filters.groups.labels.location'),
					variant: 'text',
				},
				size: 100,
			},
			{
				id: 'orderStatusIcon',
				size: 50,
				cell: ({ row }) => {
					if (!row.original.hasComments) {
						return <Box style={{ width: '24px', height: '24px' }} />;
					}
					return (
						<OrderCommentStatusDisplay
							purchaseOrderNumber={row.original.purchaseOrderNumber}
							orderCommentStatus={row.original.orderCommentStatus}
						/>
					);
				},
			},
		],
	});

	function renderEmpty() {
		return (
			<Stack align='center'>
				<Title order={3}>{t('noPendingOrdersCongratulations')}</Title>
				<Image radius='lg' src={NoPendingApproval} w={150} fit='contain' />
				<Title order={3}>{t('noPendingOrdersMessage')}</Title>
			</Stack>
		);
	}

	return (
		<Stack>
			<Group justify='space-between'>
				<Title px='md' order={4}>
					{t('pendingApproval')}
				</Title>
				<Link to='/approvals/orders' title={t('viewAllOrderApprovals')} px='md'>
					<Search data-testid='search-approval-orders' c='bright' />
				</Link>
			</Group>
			<Paper mx='md' p='sm' withBorder>
				<DataTable
					hidePageSize
					hidePagination={!table.getCanNextPage() && !table.getCanPreviousPage()}
					table={table}
					variant='apple-table'
					minWidth='500px'
					loading={ordersQuery.isFetching}
					renderEmpty={renderEmpty}
				/>
			</Paper>
		</Stack>
	);
}
